<script lang="ts">
  // Module dependencies
  import { AbTestService } from '@centerhealth/fe/abtest';
  import {
    CenterModal,
    CenterPixel,
    Footer,
    Header,
    ScoutButtonClone,
    CoreLayout,
  } from '@centerhealth/fe/center-kit';
  import {
    deviceAutoSelected,
    displayedMeter,
    emojiVariant,
    meterStore,
    pixelInitialised,
    queryParameters,
  } from '@centerhealth/fe/stores';
  import { trackEvent } from '@centerhealth/fe/utils';
  // SvelteKit dependencies
  import { browser, dev } from '$app/environment';
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';
  import _ from '@centerhealth/common/lodash';
  import { detectDeviceType } from '@centerhealth/fe/browser';
  import { SvelteToast } from '@centerhealth/fe/toast';
  import { onMount } from 'svelte';
  // Hide nav,footer, and extra if the page is embedded in webapp
  import { accessoriesStore } from '$lib';

  const initializePhoneDetection = () => {
    // Auto detects a device
    const { meterType } = detectDeviceType({
      win: window,
      strict: !dev,
    });

    // Set the selected meter if a device type is found
    if (meterType) {
      meterStore.set(meterType);
      deviceAutoSelected.set(true);
    }
  };

  let payerProvider: 'payerProvider' | 'control' = 'control';

  let providerFooterVariation: 'withProvider' | 'control' = 'control';

  let payerFooterVariation: 'withPayer' | 'control' = 'control';

  let patientsHeaderVariation: 'withPatients' | 'control' = 'control';

  let showCtaHeaderBanner: 'withCtaHeaderBanner' | 'control' = 'control';

  $: headerNavList = [
    {
      name: 'Patients',
      link: '/',
      show:
        patientsHeaderVariation === 'withPatients' &&
        ($page.url.pathname === '/organizations' || $page.url.pathname === '/providers'),
    },
    {
      name: 'Our App',
      link: '/features',
      show: true,
    },
    {
      name: 'Reviews',
      link: '/reviews',
      show: true,
    },
    {
      name: 'Providers',
      link: '/providers',
      show: payerProvider === 'payerProvider',
    },
    {
      name: 'Payers',
      link: '/organizations',
      show: payerProvider === 'payerProvider',
    },
    {
      name: 'FAQ',
      link: 'https://help.center.health/',
      show: true,
    },
    {
      name: 'Login',
      link: '/login',
      show: true,
    },
  ]
    .filter(({ show }) => show)
    .map((linksData) => ({
      ...linksData,
      active: $page.url.pathname === linksData.link,
    }));

  $: footerNavList = [
    {
      name: 'Our App',
      link: '/features',
      show: true,
    },
    {
      name: 'Reviews',
      link: '/reviews',
      show: true,
    },
    {
      name: 'Patients',
      link: '/',
      show:
        patientsHeaderVariation === 'withPatients' &&
        ($page.url.pathname === '/organizations' || $page.url.pathname === '/providers'),
    },
    {
      name: 'Providers',
      link: '/providers',
      show: payerProvider === 'payerProvider' || providerFooterVariation === 'withProvider',
    },
    {
      name: 'Payers',
      link: '/organizations',
      show: payerProvider === 'payerProvider' || payerFooterVariation === 'withPayer',
    },
    {
      name: 'FAQ',
      link: 'https://help.center.health/',
      show: true,
    },
    {
      name: 'About',
      link: '/about',
      show: true,
    },
    {
      name: 'Login',
      link: '/login',
      show: true,
    },
    {
      name: 'Contact',
      link: 'mailto:support@center.health',
      show: true,
    },
    {
      name: 'Terms',
      link: '/terms',
      show: true,
    },
    {
      name: 'Privacy',
      link: '/privacy',
      show: true,
    },
  ]
    .filter(({ show }) => show)
    .map((linksData) => ({
      ...linksData,
      active: $page.url.pathname === linksData.link,
    }));

  let trackPages = false;

  onMount(async () => {
    // Persist params to store if found in path
    if ($page.url.searchParams.toString()) {
      queryParameters.set(`?${$page.url.searchParams.toString()}`);
    }

    trackPages = true;

    emojiVariant.set(await AbTestService.getVariation('homeEmoji_22-07-21'));

    // Initialize detect user device type
    initializePhoneDetection();

    // listen to onScroll event:
    window.addEventListener('scroll', handleScroll);

    // Initialise A/B tests
    payerProvider = await AbTestService.getVariation<'control' | 'payerProvider'>(
      'payerProvider_22-07-06'
    );

    providerFooterVariation = await AbTestService.getVariation<'control' | 'withProvider'>(
      'providerFooter_XX-XX-XX'
    );
    payerFooterVariation = await AbTestService.getVariation<'control' | 'withPayer'>(
      'payerFooter_XX-XX-XX'
    );

    patientsHeaderVariation = await AbTestService.getVariation<'control' | 'withPatients'>(
      'showPatientsOnHeader_XX-XX-XX'
    );

    showCtaHeaderBanner = await AbTestService.getVariation<'control' | 'withCtaHeaderBanner'>(
      'showCtaHeaderBanner_23-01-19'
    );

    // A/B test cta in /features
    const featuresCtaVariation = await AbTestService.getVariation('featureCta_22-08-24');
    if (featuresCtaVariation === 'linkSubscribe') {
      featuresCta.link = '/subscribe';
    }
  });

  let position: 'sticky' | 'absolute' | 'relative' = 'absolute';

  const handleScroll = () => {
    if (window.scrollY <= 15) {
      position = 'absolute';
    } else {
      position = 'sticky';
    }
  };

  $: searchParams = $page.url.searchParams;

  $: {
    // track pages under this layout
    if (trackPages && $pixelInitialised && $page.url.pathname) {
      trackEvent('PageView');

      // If on a specific page, also trigger event
      let pageEvent;
      switch ($page.url.pathname) {
        case '/':
          pageEvent = 'ViewContent';
          break;
        case '/subscribe':
          pageEvent = 'InitiateCheckout';
          break;
        default:
          break;
      }

      // Wait for initialization of pageview event
      if (_.isDefined(pageEvent)) {
        setTimeout(() => {
          // ViewContent, InitiateCheckout
          trackEvent(pageEvent, {
            // content_id: '50', // TODO: replace with dynamic value
            content_type: 'product',
            // value: (31.0).toFixed(2), // TODO: replace with dynamic value
            currency: 'USD',
          });
        }, 1000);
      }
    }
  }

  // call to action button on nav
  const featuresCta = {
    label: 'Get started now ➡️',
    link: `/signup${$queryParameters}`,
  };

  $: cta = {
    label:
      $page.url.pathname === '/features'
        ? featuresCta.label
        : $page.url.pathname === '/providers'
        ? 'Sign up'
        : $page.url.pathname === '/organizations'
        ? 'Get in touch'
        : `Order your ${$displayedMeter} ${$emojiVariant === 'control' ? '💝' : ''}`,
    link:
      $page.url.pathname === '/features'
        ? featuresCta.link
        : $page.url.pathname === '/providers'
        ? '/providers#carer-signup'
        : $page.url.pathname === '/organizations'
        ? '/organizations#contact-us'
        : `/subscribe${$queryParameters}`,
  };
</script>

<CoreLayout>
  <CenterPixel />

  {#if $accessoriesStore.embeddedShop}
    <main>
      <slot />
    </main>
  {:else}
    <Header
      showStickyBanner={$page.url.pathname === '/' && showCtaHeaderBanner === 'withCtaHeaderBanner'}
      {position}
      navItems={headerNavList}
      {cta}
    />
    <main>
      <slot />
    </main>
    <Footer navItems={footerNavList} />
    <ScoutButtonClone svgName="chat-beacon" btnText="Help" />
  {/if}
  <CenterModal {goto} queryParams={searchParams} {browser} />
  <SvelteToast />
</CoreLayout>

<style>
  main {
    max-width: 1650px;
    min-width: 320px;
    margin: 0 auto !important;
  }
</style>
